/**
 * @file src/lib/dynamic.tsx
 * @exports default (dynamic)
 * @summary Drop-in replacement for `next/dynamic` built on `React.lazy` + `Suspense`.
 * @remarks Supports loaders resolving to either a module (`{ default }`) or a bare component
 *          (e.g. `import(…).then((mod) => mod.NotFound)`), plus the `loading` option.
 *          The `ssr` option is accepted and ignored — there is no server rendering in the SPA.
 */

import React, { Suspense } from "react";

type ComponentModule<P> = { default: React.ComponentType<P> };
type Loader<P> = () => Promise<ComponentModule<P> | React.ComponentType<P>>;

interface DynamicOptions {
	loading?: React.ComponentType;
	ssr?: boolean;
}

const dynamic = <P extends object>(loader: Loader<P>, options?: DynamicOptions): React.ComponentType<P> => {
	const LazyComponent = React.lazy(async () => {
		const loaded = await loader();
		if (typeof loaded === "object" && loaded !== null && "default" in loaded) {
			return loaded as ComponentModule<P>;
		}
		return { default: loaded as React.ComponentType<P> };
	});
	const Loading = options?.loading;

	const DynamicComponent = (props: P) => (
		<Suspense fallback={Loading ? <Loading /> : null}>
			<LazyComponent {...(props as React.ComponentProps<typeof LazyComponent>)} />
		</Suspense>
	);

	return DynamicComponent;
};

export default dynamic;
